/**
 * 分类占比树（**K 域 K2**）—— 一级 / 二级 + 占比条 + 合计恒等
 *
 * 依据：K 域 PRD **§4.3** · `AC-K2.1`（两级、下钻不超 1 层）· `AC-K2.4`（合计 ≡ 总支出）·
 * `AC-K2.12`（分类色取 `--cat-*`，**禁硬编码 hex**）· `AC-K2.14`（0 元占位排末尾 —— **顺序由服务端给**）·
 * `AC-K2.2`（未分类是叶子，**不当父节点**）。
 *
 * ## 🔴 本组件一个数都不算
 *
 * - **金额 / 占比**：服务端 `amount` / `shareE4` → `<Amount>` 与 `percentText`（只排版）；
 * - **顺序**：服务端已排好（金额降序、哨兵置底）⇒ **端侧不重排**（重排 = 第二份排序口径）；
 * - **条形宽度**：⚠️ 唯一的端侧算术 —— 用 `divideExact` 把占比放大成条宽百分比，
 *   它**不参与任何金额显示**（与 `PlanPage` 的进度条同一处置）。
 *
 * ✅ **下钻抽屉已于批 `60` 接上**（`AC-K2.9` / `AC-K2.10`）：`onDrill` 传入时每行变成可点入口，
 * 宿主 = `pages/ReportPage.tsx` 的 `ui/ReportCategoryDrawer.tsx`（"按分类 + 窗口取流水"**复用
 * C 域同一条流水查询**，不在这里拼第二份）。**未传 `onDrill` 时行是纯文本**（不给点不动的按钮）。
 */

import { Alert, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { divideExact, type CategoryNodeView } from '@jiaying/shared';
import { Amount } from '../ui/Amount.js';
import { AMORTIZED_ONLY_HINT, EMPTY_SPENT_HINT, categoryRows } from '../features/report-labels.js';

export function ReportCategoryTree({
  nodes,
  currency,
  totalCents,
  fxIncomplete,
  onDrill,
}: {
  nodes: readonly CategoryNodeView[];
  currency: string;
  totalCents: number;
  fxIncomplete: boolean;
  /**
   * 🔴 **下钻某个分类**（`AC-K2.9` / `AC-K2.10` / `AC-K3.3`）。
   *
   * 传了 ⇒ 每一行变成**可点**的（真正的下钻入口）；不传 ⇒ 行是纯文本。
   * （同「不画点不动的控件」纪律：宿主没接就不给入口，而不是给个点了没反应的按钮。）
   */
  onDrill?: (node: { id: string; name: string; level: 1 | 2 }) => void;
}) {
  const rows = categoryRows(nodes);

  if (rows.length === 0) {
    return (
      <Alert severity="info" data-testid="category-empty">
        {EMPTY_SPENT_HINT}
      </Alert>
    );
  }

  return (
    <Card data-testid="report-category-tree">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          分类占比
        </Typography>
        {/* 🔴 口径角标（`AC-K2.9` 的同一条纪律：卡片金额与下钻口径不同，必须让用户看得见） */}
        <Typography variant="caption" color="text.secondary" component="div">
          口径：计入收支的支出腿 − 冲减腿 + 待摊摊销
        </Typography>
        {onDrill === undefined ? null : (
          <Typography variant="caption" color="text.secondary" component="div" data-testid="drill-hint">
            点任一分类可看该分类的**流水明细**（明细口径 ≠ 上面的口径，见抽屉里的说明）
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          {rows.map((r) => (
            <Box key={`${String(r.level)}-${r.id}`} sx={{ mb: 0.75 }} data-testid={`category-row-${r.id}`}>
              {/*
                🔴 行首是下钻入口（`AC-K2.9`）—— 端侧**只转发**分类 id/名/层级，
                「按分类 + 窗口取流水」由抽屉调**同一条**流水查询（不在这里拼第二份查询）。
              */}
              <Box
                component={onDrill === undefined ? 'div' : 'button'}
                {...(onDrill === undefined
                  ? {}
                  : {
                      type: 'button' as const,
                      onClick: () => {
                        onDrill({ id: r.id, name: r.name, level: r.level });
                      },
                      'data-testid': `drill-${r.id}`,
                    })}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  p: 0,
                  font: 'inherit',
                  color: 'inherit',
                  cursor: onDrill === undefined ? 'default' : 'pointer',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography
                    variant="body2"
                    sx={{ pl: r.level === 2 ? 2 : 0, fontWeight: r.level === 1 ? 600 : 400 }}
                  >
                    {r.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="caption" color="text.secondary">
                      {r.shareText}
                    </Typography>
                    <Amount cents={r.amountCents as never} currency={currency} variant="caption" colored={false} />
                  </Stack>
                </Stack>
              </Box>
              <Box sx={{ mt: 0.25, height: 6, borderRadius: 3, bgcolor: 'action.hover' }}>
                <Box
                  sx={{
                    width: `${String(percentOf(r.amountCents, totalCents))}%`,
                    height: '100%',
                    borderRadius: 3,
                    // 🔴 分类色走令牌（`--cat-*`）—— 本批用中性面色，避免硬编码 hex（`AC-K2.12`）
                    bgcolor: r.level === 1 ? 'primary.main' : 'primary.light',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* 🔴 合计恒等（`AC-K2.4`）—— 数字 = 服务端 `totalExpense`，端侧不求和 */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}
          data-testid="category-total"
        >
          <Typography variant="caption" color="text.secondary">
            合计（= 窗口总支出）
          </Typography>
          <Amount cents={totalCents as never} currency={currency} variant="body2" colored={false} />
        </Stack>

        {fxIncomplete ? (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="category-fx-notice">
            有支出因缺少当日汇率未被折算，本币合计不完整。
          </Alert>
        ) : null}

        {/* 下钻空态的**解释文案**先就位（`AC-K2.10`）—— 宿主随下钻接线一起上 */}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {AMORTIZED_ONLY_HINT}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** ⚠️ 唯一的端侧算术：占比 → 条宽百分比（**不参与金额显示**；端侧禁 `/`，走 `divideExact`） */
function percentOf(part: number, total: number): number {
  if (total <= 0) return 0;
  return divideExact(part * 100, total);
}
